'use client'

import { useEffect, useState } from 'react'
import Script from 'next/script'

/**
 * PostHog Analytics Provider (Cookieless)
 * 
 * Implements PostHog analytics in cookieless mode for GDPR compliance.
 * Uses memory-based persistence instead of cookies.
 * Only initializes in production to ignore local/development visits.
 */
export function PostHogProvider() {
  const [shouldLoad, setShouldLoad] = useState(false)

  useEffect(() => {
    // Check environment and hostname on client side only
    const isProduction = process.env.NODE_ENV === 'production'
    const hostname = window.location.hostname
    
    // Don't load if:
    // - Not in production mode
    // - On localhost
    // - On 127.0.0.1
    // - On .local domains
    const isLocalhost = 
      hostname === 'localhost' || 
      hostname === '127.0.0.1' ||
      hostname.includes('.local') ||
      hostname.includes('localhost:')

    // Only load in production and not on localhost
    if (isProduction && !isLocalhost) {
      setShouldLoad(true)
    }
  }, [])

  // Don't render script if we shouldn't load PostHog
  if (!shouldLoad) {
    return null
  }

  return (
    <Script
      id="posthog-init"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          !function(t,e){var o,n,p,r;e.__SV||(window.posthog && window.posthog.__loaded)||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init Rr Mr fi Cr Ar ci Tr Fr capture Mi calculateEventProperties Lr register register_once register_for_session unregister unregister_for_session Hr getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey displaySurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Ur jr createPersonProfile zr kr Br opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing get_explicit_consent_status is_capturing clear_opt_in_out_capturing Dr debug M Nr getPageViewId captureTraceFeedback captureTraceMetric $r".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
          
          posthog.init('phc_ehAV1oXVELsLPpxrEBdvUWU1iFHLwAkX1XM2PSKJlc9', {
            api_host: 'https://eu.i.posthog.com',
            ui_host: 'https://eu.i.posthog.com',
            persistence: 'memory', // Cookieless mode - stores data in memory only
            persistence_name: '', // Empty name prevents cookie/localStorage usage
            disable_persistence: false, // We use 'memory' persistence instead
            disable_session_recording: false, // Can enable if needed
            person_profiles: 'identified_only', // Only create profiles for identified users
            autocapture: true, // Automatically capture clicks, form submissions, etc.
            capture_pageview: true, // Automatically capture page views
            capture_pageleave: true, // Capture when user leaves page
            loaded: function(posthog) {
              // PostHog is loaded and ready (only in production)
              // Silent in production - no console output
              // Note: process.env is not available in browser, so we check window location instead
              try {
                if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.includes('.local'))) {
                  console.log('[PostHog] Initialized in cookieless mode (production only)')
                }
              } catch (e) {
                // Silently ignore - we're in production
              }
            }
          });
        `,
      }}
    />
  )
}

